import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Play, FileSearch, AlertTriangle, CheckCircle2, ArrowRightLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminNormalizarEntregado() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const dryRun = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/normalizar-entregado/dry-run");
      setReport(data);
      toast.message(`Informe: ${data.total_candidatos} componente(s) a normalizar`);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const apply = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/normalizar-entregado/apply");
      setReport(data);
      toast.success(`Aplicado: ${data.registros_actualizados} componente(s) actualizado(s)`);
      setConfirm(false);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5" data-testid="page-admin-normalizar-entregado">
      <div className="flex items-start gap-3">
        <div className="bg-emerald-500/15 border border-emerald-500/30 rounded-lg p-2.5">
          <ArrowRightLeft className="w-6 h-6 text-emerald-300" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-zinc-100">Normalizar estado <code className="text-emerald-300">entregado</code> → <code className="text-emerald-300">recibido_almacen</code></h1>
          <p className="text-sm text-zinc-400 mt-0.5 leading-snug max-w-3xl">
            Históricamente algunos componentes con origen <strong>Proveedor → Nave</strong>, <strong>Cliente → Nave</strong> o
            <strong> Producción propia</strong> se marcaron como <code>entregado</code> (estado semánticamente reservado para
            origen <code>Cliente → PDV</code>). El fix de código ya hace que aparezcan en rutas igualmente, pero esta migración
            limpia el dato histórico para que sea coherente.
          </p>
        </div>
      </div>

      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
            <FileSearch className="w-4 h-4 text-violet-300" /> Paso 1 · Informe (dry-run)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button onClick={dryRun} disabled={loading} data-testid="btn-dryrun"
            className="bg-violet-500 hover:bg-violet-400 text-white">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSearch className="w-4 h-4 mr-2" />}
            Generar informe
          </Button>
        </CardContent>
      </Card>

      {report && (
        <Card className="bg-zinc-900/60 border-zinc-800" data-testid="report-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-300 flex items-center justify-between gap-2 flex-wrap">
              <span>Informe</span>
              <div className="flex gap-2 flex-wrap">
                <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs">
                  {report.total_candidatos} candidatos
                </Badge>
                {Object.entries(report.por_origen || {}).map(([o, n]) => (
                  <Badge key={o} className="bg-zinc-800 text-zinc-300 text-[10px]">{o}: {n}</Badge>
                ))}
                {report.applied && (
                  <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Aplicado: {report.registros_actualizados}
                  </Badge>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {report.total_candidatos === 0 ? (
              <div className="text-sm text-emerald-300 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> No hay nada que normalizar. Todos los componentes están coherentes.
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[60vh]">
                <table className="w-full text-xs">
                  <thead className="text-zinc-500 text-[10px] uppercase border-b border-zinc-800 sticky top-0 bg-zinc-900">
                    <tr>
                      <th className="text-left py-2 px-2">Petición</th>
                      <th className="text-left py-2 px-2">Estado pet.</th>
                      <th className="text-left py-2 px-2">Cliente</th>
                      <th className="text-left py-2 px-2">Componente</th>
                      <th className="text-left py-2 px-2">Origen</th>
                      <th className="text-center py-2 px-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.items.slice(0, 500).map((c) => (
                      <tr key={c.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                        <td className="py-1.5 px-2 text-amber-300 whitespace-nowrap">
                          <Link to={`/peticiones?codigo=${c.peticion_codigo}`} target="_blank">
                            {c.peticion_codigo || "—"}
                          </Link>
                        </td>
                        <td className="py-1.5 px-2 text-zinc-400">{c.peticion_estado || "—"}</td>
                        <td className="py-1.5 px-2 text-zinc-400">{c.cliente_nombre || "—"}</td>
                        <td className="py-1.5 px-2 text-zinc-200">{c.nombre}</td>
                        <td className="py-1.5 px-2 text-zinc-500">{c.origen}</td>
                        <td className="py-1.5 px-2 text-center">
                          <span className="text-zinc-500">entregado</span>
                          <span className="text-zinc-600 mx-1">→</span>
                          <span className="text-emerald-300">recibido_almacen</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {report.items.length > 500 && (
                  <div className="text-[11px] text-zinc-500 text-center mt-2">
                    Mostrando 500 de {report.items.length}.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {report && report.total_candidatos > 0 && !report.applied && (
        <Card className="bg-red-500/[0.04] border-red-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-300" />
              Paso 2 · Aplicar cambios (irreversible)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-xs text-zinc-400">
            <p>
              Vas a actualizar <strong className="text-amber-300">{report.total_candidatos}</strong> componente(s)
              cambiando su estado de <code className="text-amber-300">entregado</code> a <code className="text-emerald-300">recibido_almacen</code>.
              Esto NO modifica precios, m², ni nada más. Solo el campo <code>estado</code>.
            </p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)}
                data-testid="confirm-checkbox"
                className="w-4 h-4 accent-red-400" />
              <span className="text-zinc-200">He revisado el informe y confirmo aplicar los {report.total_candidatos} cambios.</span>
            </label>
            <Button onClick={apply} disabled={!confirm || loading} data-testid="btn-apply"
              className="bg-red-500 hover:bg-red-400 text-white disabled:opacity-40">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Aplicar normalización
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
